import { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    Box,
    Typography,
    Divider,
    Alert
} from '@mui/material';
import {
    FolderOpen as FolderIcon,
    Save as SaveIcon,
    FileCopy as CopyIcon
} from '@mui/icons-material';

/**
 * SaveLocationPicker Component
 * Let users choose where to save the signed PDF in Nextcloud
 * 
 * @param {boolean} open - Dialog open state  
 * @param {function} onClose - Close handler
 * @param {function} onConfirm - Confirm handler (receives save options)
 * @param {string} sourcePath - Original PDF path in Nextcloud
 */
export const SaveLocationPicker = ({ 
    open, 
    onClose, 
    onConfirm,
    sourcePath = ''
}) => {
    const [saveOption, setSaveOption] = useState('same_folder');
    const [customPath, setCustomPath] = useState('');
    const [overwrite, setOverwrite] = useState(false);

    /**
     * Handle confirm
     */
    const handleConfirm = () => {
        const options = {
            saveOption,
            customPath: saveOption === 'custom_path' ? customPath : null,
            overwrite: saveOption === 'same_folder' && overwrite
        };

        onConfirm(options);
        onClose();
    };

    /**
     * Reset state when dialog closes
     */
    const handleClose = () => {
        setSaveOption('same_folder');
        setCustomPath('');
        setOverwrite(false);
        onClose();
    };

    /**
     * Get source folder path
     */
    const getSourceFolder = () => {
        if (!sourcePath) return '/';
        const parts = sourcePath.split('/');
        parts.pop(); // Remove filename
        return parts.join('/') || '/';
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SaveIcon color="primary" />
                    <Typography variant="h6">
                        ¿Dónde guardar el PDF firmado?
                    </Typography>
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent>
                {sourcePath && (
                    <Alert severity="info" sx={{ marginBottom: 3 }}>
                        <Typography variant="body2">
                            <strong>Archivo original:</strong><br />
                            {sourcePath}
                        </Typography>
                    </Alert>
                )}

                <RadioGroup
                    value={saveOption}
                    onChange={(e) => setSaveOption(e.target.value)}
                >
                    {/* Same folder option */}
                    <Box 
                        sx={{ 
                            border: '1px solid',
                            borderColor: saveOption === 'same_folder' ? 'primary.main' : 'divider',
                            borderRadius: 2,
                            padding: 2,
                            marginBottom: 2,
                            cursor: 'pointer',
                            '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: 'action.hover'
                            }
                        }}
                        onClick={() => setSaveOption('same_folder')}
                    >
                        <FormControlLabel
                            value="same_folder"
                            control={<Radio />}
                            label={
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <FolderIcon fontSize="small" />
                                        <Typography variant="body1" fontWeight={600}>
                                            Misma carpeta que el original
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ marginLeft: 4 }}>
                                        {getSourceFolder()}
                                    </Typography>
                                </Box>
                            }
                        />

                        {saveOption === 'same_folder' && (
                            <Box sx={{ marginLeft: 4, marginTop: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Radio 
                                            checked={overwrite}
                                            onChange={(e) => setOverwrite(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            Sobrescribir archivo original
                                        </Typography>
                                    }
                                />
                                <FormControlLabel
                                    control={
                                        <Radio 
                                            checked={!overwrite}
                                            onChange={(e) => setOverwrite(!e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            Crear nuevo archivo (_firmado)
                                        </Typography>
                                    }
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Custom path option */}
                    <Box 
                        sx={{ 
                            border: '1px solid',
                            borderColor: saveOption === 'custom_path' ? 'primary.main' : 'divider',
                            borderRadius: 2,
                            padding: 2,
                            cursor: 'pointer',
                            '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: 'action.hover'
                            }
                        }}
                        onClick={() => setSaveOption('custom_path')}
                    >
                        <FormControlLabel
                            value="custom_path"
                            control={<Radio />}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CopyIcon fontSize="small" />
                                    <Typography variant="body1" fontWeight={600}>
                                        Ruta personalizada
                                    </Typography>
                                </Box>
                            }
                        />

                        {saveOption === 'custom_path' && (
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="/Documentos/Firmados/archivo.pdf"
                                value={customPath}
                                onChange={(e) => setCustomPath(e.target.value)}
                                sx={{ marginLeft: 4, marginTop: 1, width: 'calc(100% - 32px)' }}
                                helperText="Ingresa la ruta completa donde guardar el archivo"
                                onClick={(e) => e.stopPropagation()}
                            />
                        )}
                    </Box>
                </RadioGroup>

                <Alert severity="warning" sx={{ marginTop: 3 }}>
                    <Typography variant="caption">
                        El archivo firmado se guardará automáticamente en Nextcloud cuando todos los firmantes completen sus firmas.
                    </Typography>
                </Alert>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ padding: 2 }}>
                <Button onClick={handleClose} color="inherit">
                    Cancelar
                </Button>
                
                <Button 
                    onClick={handleConfirm} 
                    variant="contained"
                    disabled={saveOption === 'custom_path' && !customPath}
                >
                    Confirmar
                </Button>
            </DialogActions>
        </Dialog>
    );
};
